// 1.0.0: YUIChat 项目 - 认证服务
// 1.0.1: 支持无 Supabase 配置的 UI 预览模式
import { supabase, isSupabaseAvailable } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import { logger } from '../utils/logger';

const PY_BACKEND_URL = import.meta.env.VITE_PY_BACKEND_URL || 'http://localhost:8000';

export interface AuthError {
  message: string;
  code?: string;
}

async function getBackendErrorMessage(response: Response): Promise<string> {
  try {
    const errorData = await response.json();
    return errorData?.message || errorData?.detail || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

/**
 * Sign up with email and password
 * 1.3.86: 添加 emailRedirectTo 配置，邮件确认后跳转到登录页面
 */
export async function signUpWithEmail(
  email: string,
  password: string
): Promise<{ user: User | null; error: AuthError | null }> {
  try {
    // 1.3.86: 邮件确认后重定向到登录页面
    const emailRedirectTo = `${window.location.origin}/auth`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo,
      },
    });

    if (error) {
      return {
        user: null,
        error: {
          message: error.message,
          code: error.status?.toString(),
        },
      };
    }

    return {
      user: data.user,
      error: null,
    };
  } catch (error: any) {
    return {
      user: null,
      error: {
        message: error.message || '注册失败，请重试',
      },
    };
  }
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(
  email: string,
  password: string
): Promise<{ user: User | null; error: AuthError | null }> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return {
        user: null,
        error: {
          message: error.message,
          code: error.status?.toString(),
        },
      };
    }

    return {
      user: data.user,
      error: null,
    };
  } catch (error: any) {
    return {
      user: null,
      error: {
        message: error.message || '登录失败，请重试',
      },
    };
  }
}

/**
 * Sign in with Google OAuth
 */
// 1.2.57: 修复 Google 登录后重定向到错误域名的问题
export async function signInWithGoogle(): Promise<{ error: AuthError | null }> {
  try {
    // 1.2.57: 使用当前域名作为重定向目标，确保登录后返回正确的域名
    const redirectTo = window.location.origin;
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
      },
    });

    if (error) {
      return {
        error: {
          message: error.message,
          code: error.status?.toString(),
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    return {
      error: {
        message: error.message || 'Google 登录失败，请重试',
      },
    };
  }
}

/**
 * Request password reset email (via backend proxy)
 */
export async function requestPasswordReset(
  email: string
): Promise<{ error: AuthError | null }> {
  try {
    const response = await fetch(`${PY_BACKEND_URL}/api/auth/password-reset/request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        redirect_to: `${window.location.origin}/auth`,
      }),
    });

    if (!response.ok) {
      const message = await getBackendErrorMessage(response);
      return {
        error: {
          message,
          code: response.status.toString(),
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    return {
      error: {
        message: error.message || '发送重置邮件失败，请重试',
      },
    };
  }
}

/**
 * Confirm password reset with recovery access token (via backend proxy)
 */
export async function confirmPasswordReset(
  accessToken: string,
  newPassword: string
): Promise<{ error: AuthError | null }> {
  try {
    const response = await fetch(`${PY_BACKEND_URL}/api/auth/password-reset/confirm`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        access_token: accessToken,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      const message = await getBackendErrorMessage(response);
      return {
        error: {
          message,
          code: response.status.toString(),
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    return {
      error: {
        message: error.message || '重置密码失败，请重试',
      },
    };
  }
}

/**
 * Sign out
 */
export async function signOut(): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      return {
        error: {
          message: error.message,
          code: error.status?.toString(),
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    return {
      error: {
        message: error.message || '登出失败，请重试',
      },
    };
  }
}

/**
 * Get current user
 * 1.0.1: 支持无 Supabase 配置模式
 */
export async function getCurrentUser(): Promise<User | null> {
  if (!isSupabaseAvailable) {
    // UI preview mode: return null (no user)
    return null;
  }

  try {
    const { data: { user }, error } = await supabase.auth.getUser();
    
    if (error) {
      // 1.3.84: AuthSessionMissingError 是预期行为（未登录用户），不记录错误日志
      if (error.name !== 'AuthSessionMissingError') {
        logger.error('Error getting current user:', error);
      }
      return null;
    }
    
    return user;
  } catch (error: unknown) {
    // 1.3.84: AuthSessionMissingError 是预期行为（未登录用户），不记录错误日志
    const authError = error as { name?: string };
    if (authError.name !== 'AuthSessionMissingError') {
      logger.error('Error getting current user:', error);
    }
    return null;
  }
}

/**
 * Subscribe to auth state changes
 * 1.0.1: 支持无 Supabase 配置模式
 * 1.6.21: 添加 event 参数，让调用方可以根据事件类型决定是否需要重新加载
 */
export function onAuthStateChange(
  callback: (user: User | null, event?: string) => void
): { unsubscribe: () => void } {
  if (!isSupabaseAvailable) {
    // UI preview mode: return a no-op unsubscribe function
    callback(null, 'INITIAL');
    return {
      unsubscribe: () => {},
    };
  }

  const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
    callback(session?.user ?? null, event);
  });

  return {
    unsubscribe: () => {
      subscription.unsubscribe();
    },
  };
}

/**
 * Update user profile (display name, etc.)
 * 1.2.29: 添加用户资料更新功能
 */
export async function updateUserProfile(
  updates: { full_name?: string; [key: string]: any }
): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.updateUser({
      data: updates,
    });

    if (error) {
      return {
        error: {
          message: error.message,
          code: error.status?.toString(),
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    return {
      error: {
        message: error.message || '更新资料失败，请重试',
      },
    };
  }
}

/**
 * Update user password
 * 1.2.29: 添加密码更新功能
 */
export async function updateUserPassword(
  newPassword: string
): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      return {
        error: {
          message: error.message,
          code: error.status?.toString(),
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    return {
      error: {
        message: error.message || '更新密码失败，请重试',
      },
    };
  }
}

/**
 * Get user authentication providers
 * 1.2.29: 获取用户的登录方式（email, google 等）
 */
export function getUserProviders(user: User): string[] {
  if (!user) return [];
  
  // 从 app_metadata 或 identities 获取提供商信息
  const identities = user.identities || [];
  const providers = identities.map(identity => identity.provider);
  
  return providers;
}
